import type { Socket } from 'socket.io-client';
import socketio from '@/shared/lib/socketio';
import { socketListeners } from './listeners';

export interface SocketEventListener {
  event: string;
  handler: (...args: any[]) => void;
}

// Exported so tests can populate/reset it directly.
export const listeners: SocketEventListener[] = [...socketListeners];

// Hold the socket the handlers were bound to. Calling getSocket() again in detach() would target a
// NEW io() instance after a reconnect, leaving the old handlers bound while clearing bookkeeping.
let attachedSocket: Socket | null = null;

// registerSocketListener used to wrap every registration in emitSocketSpamTracking — .on(event,
// handler) here would silently drop that. Track the wrapped function actually bound to the socket
// (not the plain `handler`) so detach's .off(event, fn) removes the right reference.
let attachedEntries: SocketEventListener[] = [];

/**
 * Removes every socket listener this registry attached, leaving the connection itself alone. Calls off for each recorded event and handler pair, drops the socket reference and empties the entry list, deliberately leaving the batch timers running.
 *
 * @returns Nothing.
 */
export const detachSocketListeners = () => {
  if (attachedSocket) {
    attachedEntries.forEach(({ event, handler }) => attachedSocket!.off(event, handler));
    attachedSocket = null;
  }
  attachedEntries = [];
  // Deliberately does NOT cancel the batch timers — nothing cancels them today, so doing it here
  // would be a behavior change. See the plan's queue table.
};

/**
 * Binds the registry's event table to the live socket, replacing any earlier binding. Detaches first, bails out when getSocket returns nothing, then subscribes a spam-tracking wrapper per entry and records those wrappers so they can be removed later.
 *
 * @returns Nothing.
 */
export const attachSocketListeners = () => {
  detachSocketListeners();
  const socket = socketio.getSocket();
  if (!socket) return;
  attachedEntries = listeners.map(({ event, handler }) => {
    /**
     * Wraps one socket handler so every inbound frame is counted before it runs. Reports the event name to socketio.emitSocketSpamTracking, then delegates to the original handler with the same arguments.
     *
     * @param args - Arguments socket.io passes for the event, forwarded unchanged.
     * @returns Nothing.
     */
    const wrapped = (...args: any[]) => {
      socketio.emitSocketSpamTracking(event);
      return handler(...args);
    };
    socket.on(event, wrapped);
    return { event, handler: wrapped };
  });
  attachedSocket = socket;
};
